import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Download, Printer } from 'lucide-react';
import { api } from '../../lib/api';

export function FinancialStatementsPage() {
  const [reportType, setReportType] = useState<'bs' | 'pl' | 'cf'>('bs');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: trialBalance, isLoading } = useQuery({
    queryKey: ['trial-balance', date],
    queryFn: () => api.get<{ data: any[]; totals: any }>('/api/journals/trial-balance', { date }),
  });

  // 貸借対照表データの整形
  const bsData = {
    assets: trialBalance?.data.filter((r: any) => r.accountType === 'asset') || [],
    liabilities: trialBalance?.data.filter((r: any) => r.accountType === 'liability') || [],
    equity: trialBalance?.data.filter((r: any) => r.accountType === 'equity') || [],
  };

  // 損益計算書データの整形
  const plData = {
    revenue: trialBalance?.data.filter((r: any) => r.accountType === 'revenue') || [],
    expenses: trialBalance?.data.filter((r: any) => r.accountType === 'expense') || [],
  };

  const calcTotal = (items: any[], type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense') => {
    return items.reduce((sum, item) => {
      // 資産・費用は借方残高、負債・純資産・収益は貸方残高を使用
      if (type === 'asset' || type === 'expense') {
        return sum + (item.debitBalance || 0);
      } else {
        return sum + (item.creditBalance || 0);
      }
    }, 0);
  };

  const totalAssets = calcTotal(bsData.assets, 'asset');
  const totalLiabilities = calcTotal(bsData.liabilities, 'liability');
  const totalEquity = calcTotal(bsData.equity, 'equity');
  const totalRevenue = calcTotal(plData.revenue, 'revenue');
  const totalExpenses = calcTotal(plData.expenses, 'expense');
  const netIncome = totalRevenue - totalExpenses;

  const handleExportPDF = () => {
    // TODO: PDF出力実装
    alert('PDF出力機能は準備中です');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          決算書作成
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          >
            <Printer size={16} />
            印刷
          </button>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            <Download size={16} />
            PDF出力
          </button>
        </div>
      </div>

      {/* フィルター */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              決算書種類
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as 'bs' | 'pl' | 'cf')}
              className="px-3 py-2 rounded-lg border text-sm min-w-[200px]"
              style={{
                backgroundColor: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="bs">貸借対照表 (B/S)</option>
              <option value="pl">損益計算書 (P/L)</option>
              <option value="cf">株主資本等変動計算書</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              決算日
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="px-3 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="card p-6 print:shadow-none" id="financial-statement">
          {reportType === 'bs' && (
            <div>
              <h2 className="text-xl font-bold text-center mb-6" style={{ color: 'var(--text-primary)' }}>
                貸 借 対 照 表
              </h2>
              <p className="text-center mb-6" style={{ color: 'var(--text-secondary)' }}>
                {date} 現在
              </p>
              
              <div className="grid grid-cols-2 gap-8">
                {/* 資産の部 */}
                <div>
                  <h3 className="font-bold text-lg mb-4 border-b-2 pb-2" style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                    【資産の部】
                  </h3>
                  <table className="w-full">
                    <tbody>
                      {bsData.assets.map((item: any) => (
                        <tr key={item.accountId}>
                          <td className="py-1" style={{ color: 'var(--text-primary)' }}>{item.accountName}</td>
                          <td className="py-1 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                            ¥{(item.debitBalance || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 font-bold" style={{ borderColor: 'var(--border-color)' }}>
                        <td className="py-2" style={{ color: 'var(--text-primary)' }}>資産合計</td>
                        <td className="py-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                          ¥{totalAssets.toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* 負債・純資産の部 */}
                <div>
                  <h3 className="font-bold text-lg mb-4 border-b-2 pb-2" style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                    【負債の部】
                  </h3>
                  <table className="w-full">
                    <tbody>
                      {bsData.liabilities.map((item: any) => (
                        <tr key={item.accountId}>
                          <td className="py-1" style={{ color: 'var(--text-primary)' }}>{item.accountName}</td>
                          <td className="py-1 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                            ¥{(item.creditBalance || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t font-bold" style={{ borderColor: 'var(--border-color)' }}>
                        <td className="py-2" style={{ color: 'var(--text-primary)' }}>負債合計</td>
                        <td className="py-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                          ¥{totalLiabilities.toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <h3 className="font-bold text-lg mb-4 mt-6 border-b-2 pb-2" style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                    【純資産の部】
                  </h3>
                  <table className="w-full">
                    <tbody>
                      {bsData.equity.map((item: any) => (
                        <tr key={item.accountId}>
                          <td className="py-1" style={{ color: 'var(--text-primary)' }}>{item.accountName}</td>
                          <td className="py-1 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                            ¥{(item.creditBalance || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td className="py-1" style={{ color: 'var(--text-primary)' }}>当期純利益</td>
                        <td className="py-1 text-right font-mono" style={{ color: netIncome >= 0 ? 'var(--success)' : 'var(--error)' }}>
                          ¥{netIncome.toLocaleString()}
                        </td>
                      </tr>
                      <tr className="border-t font-bold" style={{ borderColor: 'var(--border-color)' }}>
                        <td className="py-2" style={{ color: 'var(--text-primary)' }}>純資産合計</td>
                        <td className="py-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                          ¥{(totalEquity + netIncome).toLocaleString()}
                        </td>
                      </tr>
                      <tr className="border-t-2 font-bold" style={{ borderColor: 'var(--border-color)' }}>
                        <td className="py-2" style={{ color: 'var(--text-primary)' }}>負債・純資産合計</td>
                        <td className="py-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                          ¥{(totalLiabilities + totalEquity + netIncome).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {reportType === 'pl' && (
            <div>
              <h2 className="text-xl font-bold text-center mb-6" style={{ color: 'var(--text-primary)' }}>
                損 益 計 算 書
              </h2>
              <p className="text-center mb-6" style={{ color: 'var(--text-secondary)' }}>
                自 期首 至 {date}
              </p>
              
              <table className="w-full max-w-2xl mx-auto">
                <tbody>
                  <tr className="border-b-2" style={{ borderColor: 'var(--border-color)' }}>
                    <td colSpan={2} className="py-2 font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                      【売上高】
                    </td>
                  </tr>
                  {plData.revenue.map((item: any) => (
                    <tr key={item.accountId}>
                      <td className="py-1 pl-4" style={{ color: 'var(--text-primary)' }}>{item.accountName}</td>
                      <td className="py-1 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                        ¥{(item.creditBalance || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t font-bold" style={{ borderColor: 'var(--border-color)' }}>
                    <td className="py-2" style={{ color: 'var(--text-primary)' }}>売上高合計</td>
                    <td className="py-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                      ¥{totalRevenue.toLocaleString()}
                    </td>
                  </tr>

                  <tr className="border-b-2" style={{ borderColor: 'var(--border-color)' }}>
                    <td colSpan={2} className="py-2 font-bold text-lg pt-6" style={{ color: 'var(--text-primary)' }}>
                      【売上原価】
                    </td>
                  </tr>
                  {plData.expenses.filter((r: any) => r.category === '営業費用' && r.accountName?.includes('原価')).map((item: any) => (
                    <tr key={item.accountId}>
                      <td className="py-1 pl-4" style={{ color: 'var(--text-primary)' }}>{item.accountName}</td>
                      <td className="py-1 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                        ¥{(item.debitBalance || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}

                  <tr className="border-b-2" style={{ borderColor: 'var(--border-color)' }}>
                    <td colSpan={2} className="py-2 font-bold text-lg pt-6" style={{ color: 'var(--text-primary)' }}>
                      【販売費及び一般管理費】
                    </td>
                  </tr>
                  {plData.expenses.filter((r: any) => !r.accountName?.includes('原価')).map((item: any) => (
                    <tr key={item.accountId}>
                      <td className="py-1 pl-4" style={{ color: 'var(--text-primary)' }}>{item.accountName}</td>
                      <td className="py-1 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                        ¥{(item.debitBalance || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t font-bold" style={{ borderColor: 'var(--border-color)' }}>
                    <td className="py-2" style={{ color: 'var(--text-primary)' }}>経費合計</td>
                    <td className="py-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                      ¥{totalExpenses.toLocaleString()}
                    </td>
                  </tr>

                  <tr className="border-t-4 border-double" style={{ borderColor: 'var(--accent-color)' }}>
                    <td className="py-4 font-bold text-lg" style={{ color: 'var(--text-primary)' }}>当期純利益</td>
                    <td className="py-4 text-right font-mono text-xl font-bold" style={{ color: netIncome >= 0 ? 'var(--success)' : 'var(--error)' }}>
                      ¥{netIncome.toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {reportType === 'cf' && (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
              <p style={{ color: 'var(--text-secondary)' }}>株主資本等変動計算書は準備中です</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

